import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, RefreshCw, Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { summaryKeys, useAISummary, useRegenerateAISummary } from '@/hooks/api/useAISummary';

interface AISummaryPanelProps {
  opportunityId: string;
  hasComparison: boolean;
  selectedProductIds?: string[];
}

function AISummarySkeleton() {
  return (
    <Card
      className="border-0"
      style={{
        background: 'linear-gradient(to bottom, hsl(211 47% 32%), hsl(211 47% 20%))',
        boxShadow: 'var(--shadow-2), var(--inset-highlight)',
      }}
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 bg-sky-300/15" />
          <Skeleton className="h-4 w-32 bg-sky-300/15" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-full bg-sky-300/15" />
          <Skeleton className="h-3 w-[90%] bg-sky-300/15" />
          <Skeleton className="h-3 w-[75%] bg-sky-300/15" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-20 bg-sky-300/15" />
          <Skeleton className="h-3 w-[85%] bg-sky-300/15" />
          <Skeleton className="h-3 w-[80%] bg-sky-300/15" />
          <Skeleton className="h-3 w-[70%] bg-sky-300/15" />
        </div>
      </CardContent>
    </Card>
  );
}

export function AISummaryPanel({
  opportunityId,
  hasComparison,
  selectedProductIds,
}: AISummaryPanelProps) {
  const {
    data: summary,
    isLoading,
    error,
  } = useAISummary(opportunityId, hasComparison, selectedProductIds);
  const regenerate = useRegenerateAISummary(opportunityId, selectedProductIds);
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);

  if (!hasComparison) return null;

  if (isLoading) return <AISummarySkeleton />;

  if (error || !summary) {
    const is503 =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status === 503
        : false;

    return (
      <Card
        className="border-0"
        style={{
          background: 'linear-gradient(to bottom, hsl(211 47% 32%), hsl(211 47% 20%))',
          boxShadow: 'var(--shadow-2), var(--inset-highlight)',
        }}
      >
        <CardContent className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-300">AI Analysis</span>
            {is503 && (
              <span className="text-xs text-slate-400 ml-2">
                — AI service unavailable. Check Snowflake Cortex configuration.
              </span>
            )}
          </div>
          {!is503 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-slate-300 hover:text-slate-100 hover:bg-sky-300/15"
              onClick={() =>
                queryClient.invalidateQueries({
                  queryKey: summaryKeys.detailWithProducts(opportunityId, selectedProductIds),
                })
              }
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Load
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-0"
      style={{
        background: 'linear-gradient(to bottom, hsl(211 47% 32%), hsl(211 47% 20%))',
        boxShadow: 'var(--shadow-2), var(--inset-highlight)',
      }}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-white" />
            <h3 className="text-sm font-semibold text-slate-100">AI Analysis</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => regenerate.mutate()}
              disabled={regenerate.isPending}
              className="rounded-full p-1.5 text-slate-400 hover:text-slate-200 hover:bg-sky-300/15 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${regenerate.isPending ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setCollapsed((prev) => !prev)}
              className="rounded-full p-1.5 text-slate-400 hover:text-slate-200 hover:bg-sky-300/15 transition-colors"
            >
              {collapsed ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronUp className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>

        {/* Collapsible content */}
        {!collapsed && (
          <>
            {/* Summary */}
            <p className="mt-3 text-sm leading-relaxed text-slate-200">{summary.summary}</p>

            {/* Insights */}
            {summary.insights.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Key Insights
                </p>
                <ul className="mt-2 space-y-1.5 list-disc list-inside">
                  {summary.insights.map((insight: string, i: number) => (
                    <li key={i} className="text-sm text-slate-200">
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
